import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useApi } from '@/contexts/AuthContext';
import { X, Send, Sparkles, User, Loader2 } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#2A6FC4', teal: '#00B4D8', slate: '#5C6B7A', bg: '#F5F7FA', white: '#FFFFFF' };

export default function AIChatPanel({ isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const api = useApi();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      if (messages.length === 0) {
        setMessages([{
          role: 'assistant',
          content: "I'm Fraime, your platform assistant. Ask me about entities, policies, claims, portfolio data, or compliance. I can see what you're viewing and provide contextual answers.",
        }]);
      }
    }
  }, [isOpen, messages.length]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const getContext = () => {
    const path = location.pathname;
    const tab = searchParams.get('tab') || '';
    const classFilter = searchParams.get('class') || 'all';

    const moduleMap = {
      '/today': 'Today', '/new-business': 'New Business', '/underwriting': 'Underwriting',
      '/renewals': 'Renewals', '/claims': 'Claims', '/policies': 'Policies',
      '/portfolio': 'Portfolio', '/finance': 'Finance', '/compliance': 'Compliance',
      '/distribution': 'Distribution', '/admin': 'Admin',
    };
    const module = moduleMap[path] || path;

    return { module, tab, class_filter: classFilter };
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const { data } = await api.post('/ai/chat', { message: userMsg, context: getContext() });
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Unable to process that request. Please try again.' }]);
      console.error('Chat error:', err?.message);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 h-screen w-[400px] z-40 flex flex-col shadow-2xl" style={{ background: C.white, borderLeft: '1px solid rgba(136,150,168,0.15)' }} data-testid="ai-chat-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: 'rgba(0,180,216,0.1)' }}>
            <Sparkles className="w-4 h-4" style={{ color: C.teal }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Fraime AI</h3>
            <p className="text-[10px]" style={{ color: C.slate }}>Context-aware assistant</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <kbd className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: C.bg, color: C.slate, border: '1px solid rgba(136,150,168,0.15)' }}>⌘J</kbd>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F5F7FA]" data-testid="chat-close">
            <X className="w-4 h-4" style={{ color: C.slate }} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-2.5 ${msg.role === 'user' ? 'justify-end' : ''}`}>
            {msg.role === 'assistant' && (
              <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(0,180,216,0.1)' }}>
                <Sparkles className="w-3.5 h-3.5" style={{ color: C.teal }} />
              </div>
            )}
            <div className={`max-w-[85%] px-3 py-2 rounded-lg text-[13px] leading-relaxed ${
              msg.role === 'user'
                ? 'bg-[#2A6FC4] text-white rounded-br-sm'
                : 'rounded-bl-sm'
            }`} style={msg.role === 'assistant' ? { background: C.bg, color: C.navy } : {}}>
              {msg.content}
            </div>
            {msg.role === 'user' && (
              <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{ background: 'rgba(42,111,196,0.1)' }}>
                <User className="w-3.5 h-3.5" style={{ color: C.blue }} />
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex gap-2.5">
            <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0" style={{ background: 'rgba(0,180,216,0.1)' }}>
              <Sparkles className="w-3.5 h-3.5" style={{ color: C.teal }} />
            </div>
            <div className="px-3 py-2 rounded-lg rounded-bl-sm" style={{ background: C.bg }}>
              <Loader2 className="w-4 h-4 animate-spin" style={{ color: C.teal }} />
            </div>
          </div>
        )}
      </div>

      {/* Suggested queries */}
      {messages.length <= 1 && (
        <div className="px-4 py-2 border-t" style={{ borderColor: 'rgba(136,150,168,0.08)' }}>
          <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: C.slate }}>Try asking</p>
          <div className="flex flex-wrap gap-1.5">
            {['What needs my attention today?', 'Portfolio exposure in NSW', 'Top broker by GWP', 'Explain MARAIS pricing'].map(q => (
              <button key={q} onClick={() => { setInput(q); setTimeout(sendMessage, 50); }}
                className="text-[11px] px-2.5 py-1 rounded-full transition-colors hover:bg-[#E8F0FE]"
                style={{ color: C.blue, border: '1px solid rgba(42,111,196,0.15)' }}>
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="px-4 py-3 border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything about your portfolio..."
            rows={1}
            className="flex-1 resize-none text-[13px] px-3 py-2 rounded-md outline-none"
            style={{ color: C.navy, background: C.bg, border: '1px solid rgba(136,150,168,0.15)', maxHeight: '80px' }}
            data-testid="chat-input"
          />
          <button onClick={sendMessage} disabled={!input.trim() || loading}
            className="p-2 rounded-md transition-colors disabled:opacity-40"
            style={{ background: C.teal, color: 'white' }}
            data-testid="chat-send">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
